import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { IConfigurationDataManager } from 'typings/dataManagers';
import { configurationsStore } from 'utils/MobxDataManager/singletons';
import { observable, computed, action, isObservableArray, makeObservable } from 'mobx';
import { EligibilityRuleFilter } from 'nomst/Stores/Configuration/eligibilityRuleFilter';
import { IEligibilityRule, IEligibilityRuleFilter } from 'typings/eligibilityRule';
import { EligibilityRuleFilters, MonthStrings, EligibilityRuleType } from 'typings/enums';

const ruleCategories: {[key in EligibilityRuleFilters]: number} = {
    [EligibilityRuleFilters.HireDate]: 1,
    [EligibilityRuleFilters.PayEffectiveDate]: 2,
    [EligibilityRuleFilters.AnniversaryDate]: 3,
    [EligibilityRuleFilters.Department]: 4,
    [EligibilityRuleFilters.WorkLocation]: 5
};

const monthNumbers: {[key in MonthStrings]: string} = {
    [MonthStrings.January]: '1',
    [MonthStrings.February]: '2',
    [MonthStrings.March]: '3',
    [MonthStrings.April]: '4',
    [MonthStrings.May]: '5',
    [MonthStrings.June]: '6',
    [MonthStrings.July]: '7',
    [MonthStrings.August]: '8',
    [MonthStrings.September]: '9',
    [MonthStrings.October]: '10',
    [MonthStrings.November]: '11',
    [MonthStrings.December]: '12'
};

const monthNumberToString: {[key: number]: MonthStrings} = {
    1: MonthStrings.January,
    2: MonthStrings.February,
    3: MonthStrings.March,
    4: MonthStrings.April,
    5: MonthStrings.May,
    6: MonthStrings.June,
    7: MonthStrings.July,
    8: MonthStrings.August,
    9: MonthStrings.September,
    10: MonthStrings.October,
    11: MonthStrings.November,
    12: MonthStrings.December
};

const anniversaryComment = 'Eligibility is based on the employee’s anniversary month.';

export class EligibilityRule {

    @observable eligibilityRuleId: Nullable<number>;
    @observable comment: Nullable<string>;
    @observable editableComment: Nullable<string>;
    @observable associatedAmountsTitle: string[];
    @observable amountCycleIds: number[];
    @observable isEditMode: boolean;
    @observable editableAssociatedAmountsTitle: string[];
    @observable isSaving: boolean;
    @observable ruleFilters: EligibilityRuleFilter[];
    @observable editableFilters: EligibilityRuleFilter[];
    @observable employeeCount: number;
    @observable isLoadingCount: boolean;
    @observable eligibilityRuleType: EligibilityRuleType;
    @observable editableRuletype: EligibilityRuleType;
    configurationDataManager: IConfigurationDataManager;
    masterAccountId: string;

    constructor(eligibilityDto: IEligibilityRule, maid: string) {
        makeObservable(this);
        this.eligibilityRuleId = eligibilityDto.eligibilityRuleId;
        this.comment = eligibilityDto.comment;
        this.editableComment = eligibilityDto.editableComment || null;
        this.amountCycleIds = eligibilityDto.amountCycleIds ?? [];
        this.associatedAmountsTitle = eligibilityDto.associatedAmountsTitle ?? [];
        this.isEditMode = !!eligibilityDto.isEditMode;
        this.configurationDataManager = dataManagerFactory.getConfigurationDataManager();
        this.masterAccountId = maid;
        this.isSaving = false;
        this.editableAssociatedAmountsTitle = [];
        this.ruleFilters = this.buildRuleFilters(eligibilityDto);
        this.editableFilters = [];
        this.employeeCount = 0;
        this.isLoadingCount = false;
        this.eligibilityRuleType = eligibilityDto.eligibilityRuleType
            ? EligibilityRuleType[eligibilityDto.eligibilityRuleType as keyof typeof EligibilityRuleType]
            : EligibilityRuleType.Annual;
        this.editableRuletype = this.eligibilityRuleType;
    }

    @computed
    get isAnnual(): boolean {
        return this.editableRuletype === EligibilityRuleType.Annual;
    }

    @computed
    get isEditInvalid(): boolean {
        const hasValidAnniversaryDate = this.isAnnual || this.hasRuleFilter(EligibilityRuleFilters.AnniversaryDate);

        // comment is only required for annual rules
        const hasValidComment = !this.isAnnual || !!this.editableComment;

        return !this.editableFilters.length
            || !hasValidAnniversaryDate
            || !hasValidComment
            || this.editableAssociatedAmountsTitle.length === 0
            || this.hasFiltersWithoutValues;
    }

    @computed
    get currentCycleConfiguration(): Configuration {
        return configurationsStore.convertedCycleSettingsViewCycle;
    }

    @computed
    get activeCycleConfiguration(): Configuration {
        return configurationsStore.convertedCycleSettingsViewCycle;
    }

    @computed
    get isCurrentCycleConfigurationActive(): boolean {
        if (!this.activeCycleConfiguration) {
            return false;
        }

        return this.currentCycleConfiguration.cycleId === this.activeCycleConfiguration.cycleId;
    }

    @computed
    get currentCycleAmountsTitle(): string[] {
        return this.currentCycleConfiguration.cycleBudgetFields.map(budgetField => budgetField.title);
    }

    @computed
    get currentAssociatedCycleAmounts(): CycleBudgetField[] {
        return this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            budgetField.eligibilityRule &&
            budgetField.eligibilityRule.eligibilityRuleId === this.eligibilityRuleId);
    }

    @computed
    get activeAssociatedCycleAmounts(): CycleBudgetField[] {
        return this.activeCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            budgetField.eligibilityRule &&
            budgetField.eligibilityRule.eligibilityRuleId === this.eligibilityRuleId);
    }

    @computed
    get alreadyAssociatedWithOtherEligibilityRuleCycleAmounts(): CycleBudgetField[] {
        return this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField => {
            if (!budgetField.eligibilityRule ||
                budgetField.eligibilityRule.eligibilityRuleId === this.eligibilityRuleId) {
                return false;
            }

            return this.editableAssociatedAmountsTitle.find(title =>
                title === budgetField.title) !== undefined;
        });
    }

    @computed
    get hasFiltersWithoutValues(): boolean {
        return this.editableFilters.some(rf => !rf.ruleCategoryValue?.length);
    }

    // Only one value for hire date and pay effective date filters
    // DB stores one row per filter per amount, so grab the first value for dates since they're all the same
    private getSingleValueFiltersFromService(filtersFromService: IEligibilityRuleFilter[]): EligibilityRuleFilter[] {
        const ruleFilters: EligibilityRuleFilter[] = [];
        const hireDate = filtersFromService.find(
            rf => rf.ruleCategoryId === ruleCategories[EligibilityRuleFilters.HireDate]);
        if (hireDate) {
            const hireDateValue =  isObservableArray(hireDate.ruleCategoryValue)
                ? hireDate.ruleCategoryValue[0]
                : hireDate.ruleCategoryValue;
            ruleFilters.push(new EligibilityRuleFilter(hireDate.ruleCategoryId, hireDateValue));
        }

        const payEffective = filtersFromService.find(
            rf => rf.ruleCategoryId === ruleCategories[EligibilityRuleFilters.PayEffectiveDate]);
        if (payEffective) {
            const payEffectiveValue =  isObservableArray(payEffective.ruleCategoryValue)
                ? payEffective.ruleCategoryValue[0]
                : payEffective.ruleCategoryValue;
            ruleFilters.push(new EligibilityRuleFilter(payEffective.ruleCategoryId, payEffectiveValue));
        }

        return ruleFilters;
    }

    private buildRuleFilters(eligibilityDto: IEligibilityRule): EligibilityRuleFilter[] {
        if (!eligibilityDto.ruleFilters) {
            return [];
        }

        const ruleFilters = this.getSingleValueFiltersFromService(eligibilityDto.ruleFilters);

        // All filters besides hire date and pay effective date can have multiple values
        const multiValueFilters = eligibilityDto.ruleFilters.filter(rf =>
            rf.ruleCategoryId !== ruleCategories[EligibilityRuleFilters.HireDate]
            && rf.ruleCategoryId !== ruleCategories[EligibilityRuleFilters.PayEffectiveDate]);

        const filterMap = new Map<number, Set<string>>();
        multiValueFilters.forEach(rf => {
            if (typeof rf.ruleCategoryValue === 'string') {
                const values = filterMap.get(rf.ruleCategoryId) || new Set<string>();
                let value = rf.ruleCategoryValue;
                // Anniversary filter values need to be converted from their number representation back to names
                if (rf.ruleCategoryId === ruleCategories[EligibilityRuleFilters.AnniversaryDate]) {
                    value = monthNumberToString[Number(value)];
                }

                values.add(value);
                filterMap.set(rf.ruleCategoryId, values);
            }
        });

        filterMap.forEach((value, key) => ruleFilters.push(new EligibilityRuleFilter(key, Array.from(value))));
        return ruleFilters;
    }

    @action
    setEditableComment(comment: Nullable<string>): void {
        this.editableComment = comment;
    }

    @action
    handleEditableAssociatedAmountsChange(value: string[]): void {
        this.editableAssociatedAmountsTitle = value;
    }

    // For use with hire date and pay effective date filters
    @action
    async setRuleFilterValue(categoryName: EligibilityRuleFilters, value: string): Promise<void> {
        const ruleFilter = this.editableFilters.find(ruleFilter => ruleFilter.ruleCategoryName === categoryName);
        if (ruleFilter) {
            ruleFilter.setValue(value);
            await this.getEmployeeCount();
        }
    }

    // For use with anniversary month and department filters
    @action
    async setRuleFilterValues(categoryName: EligibilityRuleFilters, values: string[]): Promise<void> {
        const ruleFilter = this.editableFilters.find(ruleFilter => ruleFilter.ruleCategoryName === categoryName);
        if (ruleFilter) {
            ruleFilter.setValue(values);
            await this.getEmployeeCount();
        }
    }

    @action
    addRuleFilter(categoryName: EligibilityRuleFilters): void {
        this.editableFilters.push(new EligibilityRuleFilter(ruleCategories[categoryName]));
    }

    @action
    removeRuleFilter(categoryName: EligibilityRuleFilters): Promise<void> {
        this.editableFilters = this.editableFilters.filter(rule => rule.ruleCategoryName !== categoryName);
        return this.getEmployeeCount();
    }

    hasRuleFilter(categoryName: EligibilityRuleFilters): boolean {
        return !!this.editableFilters.find(ruleFilter => ruleFilter.ruleCategoryName === categoryName);
    }

    // For use with hire date and pay effective date filters
    getEditableFilterValue(categoryName: EligibilityRuleFilters): string {
        const ruleFilter = this.editableFilters.find(ruleFilter => ruleFilter.ruleCategoryName === categoryName);
        if (typeof ruleFilter?.ruleCategoryValue !== 'string') {
            return '';
        }

        return ruleFilter.ruleCategoryValue;
    }

    // For use with anniversary month and department filters
    getEditableFilterValues(categoryName: EligibilityRuleFilters): string[] {
        const ruleFilter = this.editableFilters.find(ruleFilter => ruleFilter.ruleCategoryName === categoryName);
        if (typeof ruleFilter?.ruleCategoryValue === 'string') {
            return [];
        }

        return ruleFilter?.ruleCategoryValue ?? [];
    }

    @action
    editRule(): void {
        this.editableComment = this.comment;
        this.editableAssociatedAmountsTitle = this.associatedAmountsTitle;
        this.editableFilters = [];
        this.ruleFilters.forEach(rf =>
            this.editableFilters.push(new EligibilityRuleFilter(rf.ruleCategoryId, rf.ruleCategoryValue)));
        this.isEditMode = true;
        this.editableRuletype = this.eligibilityRuleType;
    }

    @action
    cancelEditRule(): Promise<void> {
        this.editableComment = null;
        this.editableFilters = [];
        this.isEditMode = false;
        this.editableRuletype = this.eligibilityRuleType;
        return this.getEmployeeCount();
    }

    @action
    toggleRuleType(isAnnual: boolean): Promise<void> {
        if (isAnnual === false) {
            // switched to Anniversary, clear HireDate / PayEffectiveDate inputs
            this.editableRuletype = EligibilityRuleType.Anniversary;
            this.editableFilters = this.editableFilters.filter(rule =>
                rule.ruleCategoryName !== EligibilityRuleFilters.HireDate &&
                rule.ruleCategoryName !== EligibilityRuleFilters.PayEffectiveDate);
            
            this.addRuleFilter(EligibilityRuleFilters.AnniversaryDate);
        } else {
            // switched to Annual, clear AnniversaryDate input
            this.editableRuletype = EligibilityRuleType.Annual;
            this.editableFilters = this.editableFilters.filter(rule =>
                rule.ruleCategoryName !== EligibilityRuleFilters.AnniversaryDate);
            this.editableComment = null;
        }
        return this.getEmployeeCount();
    }

    @action
    setCycleAmountsEligibilityRule(associatedAmounts: CycleBudgetField[]): void {
        associatedAmounts.forEach(amount => amount.setEligibilityRule(this));
        if (!this.isCurrentCycleConfigurationActive) {
            return;
        }

        this.activeAssociatedCycleAmounts.forEach(activeAmount => activeAmount.deleteEligibilityRule());
        this.activeCycleConfiguration.cycleBudgetFields.forEach(activeAmount => {
            if (associatedAmounts.find(amount =>
                amount.amountCode === activeAmount.amountCode) === undefined) {
                return;
            }

            activeAmount.setEligibilityRule(this);
        });
    }

    @action
    async addNewEligibilityRule(associatedAmounts: CycleBudgetField[]): Promise<void> {
        const newEligibilityRuleId = await this.configurationDataManager.addNewEligibilityRule(
            this.masterAccountId,
            this.currentCycleConfiguration.cycleId,
            {
                eligibilityRuleId: this.eligibilityRuleId,
                comment: this.comment,
                ruleFilters: this.getFiltersForService(this.ruleFilters),
                eligibilityRuleType: this.eligibilityRuleType
            } as IEligibilityRule,
            associatedAmounts.map(amount => amount.amountCycleId));

        this.eligibilityRuleId = newEligibilityRuleId;
        this.setCycleAmountsEligibilityRule(associatedAmounts);
    }

    @action
    async updateEligibilityRule(associatedAmounts: CycleBudgetField[]): Promise<void> {
        await this.configurationDataManager.updateEligibilityRule(
            this.masterAccountId,
            this.currentCycleConfiguration.cycleId,
            {
                eligibilityRuleId: this.eligibilityRuleId,
                comment: this.comment,
                ruleFilters: this.getFiltersForService(this.ruleFilters),
                eligibilityRuleType: this.eligibilityRuleType
            } as IEligibilityRule,
            associatedAmounts.map(amount => amount.amountCycleId));

        this.currentAssociatedCycleAmounts.forEach(amount => amount.deleteEligibilityRule());
        this.setCycleAmountsEligibilityRule(associatedAmounts);
    }

    @action
    async saveChanges(): Promise<void> {
        this.isSaving = true;
        this.comment = this.isAnnual ? this.editableComment : anniversaryComment;
        this.eligibilityRuleType = this.editableRuletype;
        this.associatedAmountsTitle = this.editableAssociatedAmountsTitle;
        this.ruleFilters = [...this.editableFilters];
        const associatedAmounts = this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            this.associatedAmountsTitle.find(title => title === budgetField.title) !== undefined);
        if (!this.eligibilityRuleId) {
            await this.addNewEligibilityRule(associatedAmounts);
        } else {
            await this.updateEligibilityRule(associatedAmounts);
        }

        this.isSaving = false;
        this.isEditMode = false;
    }

    @action
    async getEmployeeCount(): Promise<void> {
        this.isLoadingCount = true;
        const associatedAmounts = this.currentCycleConfiguration.cycleBudgetFields.filter(budgetField =>
            this.associatedAmountsTitle.find(title => title === budgetField.title) !== undefined);
        const ruleFilters = this.isEditMode ? this.editableFilters : this.ruleFilters;
        const filtersWithValues = ruleFilters.filter(rf =>
            (typeof rf.ruleCategoryValue === 'string' && !!rf.ruleCategoryValue) ||
            (isObservableArray(rf.ruleCategoryValue) && rf.ruleCategoryValue.length));
        if (!filtersWithValues.length) {
            this.employeeCount = 0;
        } else {
            this.employeeCount = (await this.configurationDataManager.getIneligibleEmployeeCount(
                this.masterAccountId,
                this.currentCycleConfiguration.cycleId,
                {
                    eligibilityRuleId: this.eligibilityRuleId,
                    comment: this.comment,
                    ruleFilters: this.getFiltersForService(filtersWithValues),
                    eligibilityRuleType: this.editableRuletype
                } as IEligibilityRule,
                associatedAmounts.map(amount => amount.amountCycleId)
            )) ?? 0;
        }
        this.isLoadingCount = false;
    }

    @action
    setAssociatedCycleAmountsTitle(): void {
        this.associatedAmountsTitle = this.currentAssociatedCycleAmounts?.map(amount => amount.title);
    }

    private getFiltersForService(filters: EligibilityRuleFilter[]): EligibilityRuleFilter[] {
        const anniversary = filters.find(rule => rule.ruleCategoryName === EligibilityRuleFilters.AnniversaryDate);
        const anniversaryFilters = anniversary ? this.mapAnniversaryMonths(anniversary) : [];

        const filtersForService: EligibilityRuleFilter[] = [];
        filters.forEach(filter => {
            if (filter.ruleCategoryName !== EligibilityRuleFilters.AnniversaryDate) {
                if (isObservableArray(filter.ruleCategoryValue)) {
                    filter.ruleCategoryValue.forEach(value =>
                        filtersForService.push(new EligibilityRuleFilter(filter.ruleCategoryId, value)));
                } else {
                    filtersForService.push(new EligibilityRuleFilter(filter.ruleCategoryId, filter.ruleCategoryValue));
                }
            }
        });

        return anniversaryFilters.length ? filtersForService.concat(anniversaryFilters) : filtersForService;
    }

    private mapAnniversaryMonths(anniversary: EligibilityRuleFilter): EligibilityRuleFilter[] {
        const ruleFilters: EligibilityRuleFilter[] = [];
        const monthArr = anniversary.ruleCategoryValue;
        if (isObservableArray(monthArr)) {
            const monthNumberArr = monthArr.map((month: MonthStrings) => monthNumbers[month]);
            monthNumberArr.forEach(month => ruleFilters.push(new EligibilityRuleFilter(
                ruleCategories[EligibilityRuleFilters.AnniversaryDate],
                month)));
        }

        return ruleFilters;
    }
}